/**
 * Die Spur des nativen Anrufs (CallKit), wie Server UND Seite sie lesen.
 * (06.10.2026 — vorher stand das nur in server/anruf-callkit-rueckfall.ts)
 *
 * PawbotAnruf.swift (`gemeldet`) schreibt eine Zeile, die App reicht sie über
 * das Plugin-Ereignis `anrufSpur` an die Seite, frontend/lib/anruf-nativ.ts
 * setzt „[anruf-nativ] “ davor und schickt sie ans Server-Protokoll.
 *
 * Zwei lesen dieselbe Zeile:
 *   - der Server schickt bei code=3 sofort die Meldung zum Tippen
 *     (server/anruf-callkit-rueckfall.ts),
 *   - die Seite zeigt bei code=3, wenn sie vorne ist, ihre eigene Karte
 *     „Vespera ruft an“ (frontend/lib/anruf-karte.ts).
 *
 * ⚠️ EIN Ausdruck für beide. Zwei Kopien laufen auseinander, sobald die App
 *    die Zeile um ein Feld erweitert — und dann reagiert nur noch einer.
 * ⚠️ Die Zeile trägt nur die ersten 8 Hex-Zeichen der callId (`kurz`), nie die
 *    ganze Kennung. Die ganze kennt nur der Server; die Seite fragt sie mit
 *    ihrer Sitzung ab (GET /api/anruf/klingelt, server/anruf-routen.ts).
 */

/** CXErrorCodeIncomingCallError.filteredByDoNotDisturb — ein Fokus ließ den Anruf nicht durch. */
export const CALLKIT_CODE_FOKUS = 3;

/** Die ersten 8 Hex-Zeichen einer callId, so wie die Spur sie trägt. */
export const ANRUF_PRAEFIX_FORM = /^[0-9a-f]{8}$/;

/**
 * CallKit lehnt ab: „callkit lehnt ab <8 Hex> code=<Zahl>“ und, wenn bekannt,
 * „ uhr=+2 s“.
 */
export const CALLKIT_ABLEHNUNG_FORM =
  /^\[anruf-nativ\] callkit lehnt ab ([0-9a-f]{8}) code=(\d{1,4})(?: uhr=[+-]\d{1,7} s)?$/;

/**
 * Die Gegenspur: CallKit hat den Anruf angenommen, es klingelt.
 * „klingelt <8 Hex> frist=<n> s“, dazu ggf. „ rest=<n> s“ und „ uhr=+2 s“.
 */
export const CALLKIT_KLINGELT_FORM =
  /^\[anruf-nativ\] klingelt ([0-9a-f]{8}) frist=\d{1,5} s(?: rest=-?\d{1,7} s)?(?: uhr=[+-]\d{1,7} s)?$/;

export type CallkitSpur =
  | { art: "ablehnung"; praefix: string; code: number }
  | { art: "klingelt"; praefix: string };

/** Art, Präfix (und Code) aus einer Spur-Zeile — oder null, wenn sie nicht genau passt. */
export function callkitSpurAus(zeile: unknown): CallkitSpur | null {
  if (typeof zeile !== "string") return null;
  const [, praefix, code] = CALLKIT_ABLEHNUNG_FORM.exec(zeile) ?? [];
  if (praefix && code) return { art: "ablehnung", praefix, code: Number(code) };
  const [, klingeltPraefix] = CALLKIT_KLINGELT_FORM.exec(zeile) ?? [];
  return klingeltPraefix ? { art: "klingelt", praefix: klingeltPraefix } : null;
}

/**
 * Das Präfix, wenn die Zeile sagt: ein FOKUS hat den Anruf gefiltert (code=3).
 * Jede andere Ablehnung und jede andere Zeile: null.
 */
export function fokusAblehnungAus(zeile: unknown): string | null {
  const spur = callkitSpurAus(zeile);
  return spur?.art === "ablehnung" && spur.code === CALLKIT_CODE_FOKUS ? spur.praefix : null;
}
